import React from "react";
import { Helmet } from "react-helmet";
import { Box, Text, VStack, HStack, Icon } from "@chakra-ui/react";
import { FaShieldAlt, FaHandshake, FaStar } from "react-icons/fa";

const OurPromisePage = () => {
  return (
    <>
      <Helmet>
        <title>ATO | Our Promise</title>
        <meta
          name="description"
          content="Discover ATO's promise of reliability, transparency, and customer satisfaction. Learn how we are committed to delivering exceptional service and building trust with every client."
        />
      </Helmet>
      <Box bg="gray.50" minH="100vh">
        {/* Hero Section */}
        <Box
          bg="white"
          pt={{ base: "60px", md: "100px" }}
          pb={{ base: "60px", md: "100px" }}
          px={{ base: 4, md: 8 }}
          textAlign="center"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Text
            fontSize={{ base: "4xl", md: "6xl" }}
            fontWeight="800"
            color="gray.900"
            fontFamily="'Work Sans', sans-serif"
            mb={4}
          >
            Our Promise
          </Text>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="700px"
            mx="auto"
          >
            Your trust is our foundation. Discover our commitment to excellence.
          </Text>
        </Box>

        {/* Introduction Section */}
        <Box py={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
          <Box maxW="1000px" mx="auto">
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.700"
              textAlign="center"
              lineHeight="1.8"
            >
              At <strong>ATO</strong>, we are dedicated to providing our
              customers with exceptional services and a seamless experience. We
              understand the importance of reliability, trust, and quality when
              it comes to maintaining your home or business. Our promise is
              built on a foundation of these values and goes beyond the expected
              to deliver excellence in every aspect of our service.
            </Text>
          </Box>
        </Box>

        {/* Promise Cards Section */}
        <Box bg="white" py={{ base: 16, md: 20 }} px={{ base: 4, md: 8 }}>
          <VStack spacing={8} maxW="1000px" mx="auto">
            {/* Reliable Services Card */}
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              w="100%"
              boxShadow="md"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              transition="all 0.3s ease"
            >
              <HStack spacing={4} align="start">
                <Box
                  bg="orange.50"
                  borderRadius="full"
                  w="60px"
                  h="60px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={FaShieldAlt} boxSize={8} color="orange.500" />
                </Box>
                <Box textAlign="left">
                  <Text fontSize="2xl" fontWeight="700" color="gray.900" mb={3}>
                    Reliable Services
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="1.7">
                    Our commitment is to ensure that every service we provide is
                    performed to the highest standard. From the initial
                    consultation to the final result, we strive to meet and
                    exceed your expectations. With experienced professionals,
                    on-time delivery, and a customer-first approach, we are here
                    to solve your problems efficiently and effectively.
                  </Text>
                </Box>
              </HStack>
            </Box>

            {/* Transparency & Trust Card */}
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              w="100%"
              boxShadow="md"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              transition="all 0.3s ease"
            >
              <HStack spacing={4} align="start">
                <Box
                  bg="orange.50"
                  borderRadius="full"
                  w="60px"
                  h="60px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={FaHandshake} boxSize={8} color="orange.500" />
                </Box>
                <Box textAlign="left">
                  <Text fontSize="2xl" fontWeight="700" color="gray.900" mb={3}>
                    Transparency & Trust
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="1.7">
                    We value your trust and work diligently to build and
                    maintain it. Our transparent processes and clear
                    communication mean that you will always know what to expect
                    from us. We aim to be your trusted partner, addressing your
                    concerns and tailoring our services to your specific needs.
                  </Text>
                </Box>
              </HStack>
            </Box>

            {/* Customer Satisfaction Card */}
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              w="100%"
              boxShadow="md"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              transition="all 0.3s ease"
            >
              <HStack spacing={4} align="start">
                <Box
                  bg="orange.50"
                  borderRadius="full"
                  w="60px"
                  h="60px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={FaStar} boxSize={8} color="orange.500" />
                </Box>
                <Box textAlign="left">
                  <Text fontSize="2xl" fontWeight="700" color="gray.900" mb={3}>
                    Customer Satisfaction
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="1.7">
                    Your satisfaction is our top priority. We take pride in our
                    attention to detail and the positive feedback we receive
                    from our clients. Our goal is to ensure that every
                    interaction leaves you confident in choosing ATO for all
                    your service needs.
                  </Text>
                </Box>
              </HStack>
            </Box>
          </VStack>
        </Box>

        {/* Final Statement Section */}
        <Box bg="gray.50" py={{ base: 16, md: 20 }} px={{ base: 4, md: 8 }}>
          <Box maxW="800px" mx="auto" textAlign="center">
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="700"
              color="gray.900"
              mb={6}
            >
              Our Promise to You
            </Text>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.700"
              mb={8}
              lineHeight="1.8"
            >
              We promise to always deliver reliable, high-quality services that
              you can depend on. Whether it's a small repair or a major project,
              we approach every task with dedication, precision, and care. We
              believe in building lasting relationships with our customers by
              consistently delivering value, professionalism, and trust.
            </Text>
            <Text
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="700"
              color="orange.500"
            >
              Thank you for choosing ATO. We look forward to serving you!
            </Text>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default OurPromisePage;
